import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, TOKEN_KEY, SESSION_EXPIRED_EVENT, tokenPayload } from "@/lib/api";
import { markCustomerLogin } from "@/lib/welcomeSignal";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const TOKEN_LIFE_S = 7 * 24 * 3600;
const RETRY_MAX_MS = 30000;

// Renew a customer token once it is past half its life; never stores a token after logout/replacement.
async function maybeRefresh() {
  const token = localStorage.getItem(TOKEN_KEY);
  const p = token && tokenPayload(token);
  if (!p || p.role !== "CUSTOMER" || !p.exp) return;
  if (p.exp - Date.now() / 1000 > TOKEN_LIFE_S / 2) return;
  try {
    const { data } = await api.post("/auth/refresh");
    if (data?.token && localStorage.getItem(TOKEN_KEY) === token) localStorage.setItem(TOKEN_KEY, data.token);
  } catch { /* 401 handled by interceptor; transient errors retried on next trigger */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=auth
  const [checked, setChecked] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const retry = useRef({ timer: null, delay: 2000 });

  const loadUser = useCallback(() => {
    clearTimeout(retry.current.timer);
    if (!localStorage.getItem(TOKEN_KEY)) { setUser(false); setChecked(true); setReconnecting(false); return; }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); setReconnecting(false); retry.current.delay = 2000; setChecked(true); maybeRefresh(); })
      .catch((e) => {
        const s = e?.response?.status;
        if (s === 401) { localStorage.removeItem(TOKEN_KEY); setUser(false); setReconnecting(false); setChecked(true); return; }
        if (s === 403) { setUser(false); setReconnecting(false); setChecked(true); return; }
        setReconnecting(true);
        retry.current.timer = setTimeout(loadUser, retry.current.delay);
        retry.current.delay = Math.min(retry.current.delay * 2, RETRY_MAX_MS);
      });
  }, []);

  useEffect(() => {
    loadUser();
    const onExpired = () => setUser(false);
    const onVisible = () => { if (document.visibilityState === "visible") maybeRefresh(); };
    const onOnline = () => { if (retry.current.timer) loadUser(); };
    const iv = setInterval(maybeRefresh, 60 * 60 * 1000);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(iv);
      clearTimeout(retry.current.timer);
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadUser]);

  const loginWithToken = (token, u) => {
    localStorage.setItem(TOKEN_KEY, token);
    if (u?.role === "CUSTOMER") markCustomerLogin(); // trigger the welcome overlay on this login
    setUser(u);
  };
  const logout = () => {
    clearTimeout(retry.current.timer);
    localStorage.removeItem(TOKEN_KEY);
    setReconnecting(false);
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loginWithToken, logout, checked, reconnecting, retryNow: loadUser }}>
      {children}
    </AuthCtx.Provider>
  );
}
